import { useEffect, useState, useCallback, Fragment } from 'react';
import { RefreshCw, ChevronDown, ChevronRight, AlertTriangle, Flag } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Read-only telemetry view over the `parser_events` table. This component NEVER
// mutates any row — no update/delete affordances exist here, mirroring the
// locked admin read-only decision. Column names and flag shapes below match
// src/lib/ami/parserTelemetry.ts and src/lib/ami/plausibilityValidator.ts.

// Nightly Playwright/CI runs parse fixture files against staging and pollute
// parser_events (several fixture_golden.csv rows per run), skewing the stat
// cards. We resolve these accounts to user_ids and exclude their rows from the
// stat-card aggregates and the events query — DISPLAY-SIDE ONLY. Rows are never
// deleted and the telemetry write path is never filtered; fixture telemetry
// stays recorded and remains inspectable via the "Show CI test traffic" toggle.
const CI_TEST_ACCOUNT_EMAILS = [
  'jakemarin74+paywall-agent@gmail.com', // paywall harness agent
  'jakemarin74+testing-agent@yahoo.com', // primary CI/Playwright test account
];

type ParseStatus = 'success' | 'manual_required' | 'error';
type StatusFilter = 'all' | ParseStatus;
type Severity = 'verify' | 'info';

interface ParserFlag {
  check: string;
  severity: Severity;
  message: string;
  detail: Record<string, unknown>;
}

// Layer 3 AI auditor verdict (shadow mode). Shape matches
// netlify/functions/parser-audit-background.ts. Any field may be absent.
interface AiVerdict {
  agrees?: boolean;
  disagreements?: string[];
  proposedMapping?: Record<string, unknown> | null;
  confidence?: number;
  reasoning?: string;
  model?: string;
  latencyMs?: number;
  auditedAt?: string;
  skipped?: string;
  error?: string;
  detail?: string;
}

interface ParserEventRow {
  id: string;
  created_at: string;
  user_id: string | null;
  project_id: string | null;
  utility_name: string | null;
  file_name: string | null;
  file_size_bytes: number | null;
  file_type: string | null;
  sheet_count: number | null;
  chosen_sheet: string | null;
  header_row_index: number | null;
  header_confidence: number | null;
  timestamp_column: string | null;
  demand_column: string | null;
  demand_unit: string | null;
  interval_minutes: number | null;
  row_count: number | null;
  expected_row_count: number | null;
  span_days: number | null;
  peak_kw: number | null;
  parse_status: ParseStatus | null;
  flags: ParserFlag[] | null;
  flag_count: number | null;
  user_action: string | null;
  corrected_demand_column: string | null;
  ai_verdict: AiVerdict | null;
  format_fingerprint: string | null;
}

interface UserProfileLite {
  id: string;
  email: string | null;
  full_name: string | null;
}

interface SummaryStats {
  total: number;
  flagged: number;
  disputed: number;
  manualOrError: number;
}

// The exact column set the table + expanded detail need — selected explicitly
// so we never pull unused wide columns (e.g. format_fingerprint is small; the
// wide ai_verdict jsonb is included because the AI Verdict column renders it).
const EVENT_COLUMNS =
  'id, created_at, user_id, project_id, utility_name, file_name, file_size_bytes, ' +
  'file_type, sheet_count, chosen_sheet, header_row_index, header_confidence, ' +
  'timestamp_column, demand_column, demand_unit, interval_minutes, row_count, ' +
  'expected_row_count, span_days, peak_kw, parse_status, flags, flag_count, ' +
  'user_action, corrected_demand_column, ai_verdict, format_fingerprint';

const PAGE_SIZE = 50;
const DASH = '—';

function thirtyDaysAgoISO(): string {
  return new Date(Date.now() - 30 * 86_400_000).toISOString();
}

function formatLocalTime(ts: string | null): string {
  if (!ts) return DASH;
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return DASH;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// Null / undefined render as an em dash, never blank.
function orDash(v: unknown): React.ReactNode {
  if (v === null || v === undefined || v === '') return DASH;
  return String(v);
}

function coveragePercent(actual: number | null, expected: number | null): string {
  if (actual == null || expected == null || expected <= 0) return DASH;
  return `${Math.round((actual / expected) * 1000) / 10}%`;
}

function userLabel(userId: string | null, profile: UserProfileLite | undefined): string {
  if (profile) return profile.email || profile.full_name || `${userId?.slice(0, 8)}…`;
  if (userId) return `${userId.slice(0, 8)}…`;
  return DASH;
}

function StatCard({
  label,
  value,
  sub,
  color = 'gray',
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: 'blue' | 'amber' | 'red' | 'gray';
}) {
  const bg = { blue: 'bg-blue-50', amber: 'bg-amber-50', red: 'bg-red-50', gray: 'bg-gray-50' }[color];
  const text = { blue: 'text-blue-700', amber: 'text-amber-700', red: 'text-red-700', gray: 'text-gray-700' }[color];
  return (
    <div className={`${bg} rounded-lg p-4 border border-gray-100`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-3xl font-bold ${text}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: ParseStatus | null }) {
  const config: Record<ParseStatus, { label: string; cls: string }> = {
    success: { label: 'Success', cls: 'bg-green-100 text-green-800' },
    manual_required: { label: 'Manual', cls: 'bg-amber-100 text-amber-800' },
    error: { label: 'Error', cls: 'bg-red-100 text-red-800' },
  };
  if (!status) return <span className="text-gray-400">{DASH}</span>;
  const { label, cls } = config[status];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

function SeverityBadge({ severity }: { severity: Severity }) {
  const cls = severity === 'verify' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {severity}
    </span>
  );
}

function ActionBadge({ action }: { action: string | null }) {
  if (!action) return <span className="text-gray-400">{DASH}</span>;
  const map: Record<string, string> = {
    disputed: 'bg-red-100 text-red-800',
    confirmed: 'bg-green-100 text-green-800',
    pending: 'bg-gray-100 text-gray-700',
  };
  const cls = map[action] ?? 'bg-gray-100 text-gray-700';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{action}</span>;
}

// Resolve the AI Verdict display state. `pending` = the audit was invoked
// (this is a Batch-C row, so format_fingerprint is populated) but ai_verdict is
// still null. A pre-Batch-C row has BOTH null → em dash (distinguishable).
type AiVerdictState = 'agree' | 'disagree' | 'error' | 'skipped' | 'pending' | 'none';
function aiVerdictState(row: ParserEventRow): AiVerdictState {
  const v = row.ai_verdict;
  if (!v) return row.format_fingerprint ? 'pending' : 'none';
  if (v.skipped) return 'skipped';
  if (v.error) return 'error';
  if (v.agrees === true) return 'agree';
  if (v.agrees === false) return 'disagree';
  return 'pending';
}

function AiVerdictBadge({ row }: { row: ParserEventRow }) {
  const state = aiVerdictState(row);
  if (state === 'none') return <span className="text-gray-400">{DASH}</span>;
  if (state === 'pending') return <span className="text-xs text-gray-400 italic">pending</span>;
  const config: Record<Exclude<AiVerdictState, 'none' | 'pending'>, { label: string; cls: string }> = {
    agree: { label: 'agree', cls: 'bg-gray-100 text-gray-600' },
    disagree: { label: 'DISAGREE', cls: 'bg-red-600 text-white font-semibold' },
    error: { label: 'error', cls: 'bg-orange-100 text-orange-800' },
    skipped: { label: 'skipped', cls: 'bg-gray-100 text-gray-500' },
  };
  const { label, cls } = config[state];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${cls}`}>{label}</span>;
}

function MetaField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-gray-900 mt-0.5 break-words">{value}</dd>
    </div>
  );
}

function compactDetail(detail: Record<string, unknown>): string {
  const entries = Object.entries(detail ?? {});
  if (entries.length === 0) return '{}';
  return entries
    .map(([k, v]) => `${k}: ${typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`)
    .join('  •  ');
}

function AiVerdictDetail({ verdict, state }: { verdict: AiVerdict | null; state: AiVerdictState }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
        AI Verdict <span className="font-normal text-gray-400">(shadow mode — advisory only)</span>
      </h4>
      {state === 'none' && <p className="text-sm text-gray-500">No audit for this parse (pre-Batch-C row).</p>}
      {state === 'pending' && <p className="text-sm text-gray-500">Audit invoked; verdict not yet recorded.</p>}
      {state === 'skipped' && (
        <p className="text-sm text-gray-500">Skipped — {verdict?.skipped ?? 'not audited'}.</p>
      )}
      {state === 'error' && (
        <p className="text-sm text-orange-700">
          Audit error — {verdict?.error}
          {verdict?.detail ? `: ${verdict.detail}` : ''}
        </p>
      )}
      {(state === 'agree' || state === 'disagree') && verdict && (
        <div className="bg-white rounded-md border border-gray-200 p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={state === 'disagree' ? 'font-semibold text-red-700' : 'text-gray-700'}>
              {state === 'disagree' ? 'Disagrees with the parser' : 'Agrees with the parser'}
            </span>
            {typeof verdict.confidence === 'number' && (
              <span className="text-xs text-gray-500">confidence {verdict.confidence}</span>
            )}
          </div>
          {(verdict.disagreements?.length ?? 0) > 0 && (
            <ul className="list-disc pl-5 text-sm text-red-800 space-y-0.5">
              {verdict.disagreements!.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}
          {verdict.proposedMapping && Object.keys(verdict.proposedMapping).length > 0 && (
            <p className="text-xs text-gray-600 font-mono break-words">
              proposed: {JSON.stringify(verdict.proposedMapping)}
            </p>
          )}
          {verdict.reasoning && <p className="text-sm text-gray-700">{verdict.reasoning}</p>}
          <p className="text-xs text-gray-400">
            {verdict.model ?? 'model —'}
            {verdict.latencyMs != null ? ` · ${verdict.latencyMs} ms` : ''}
            {verdict.auditedAt ? ` · ${formatLocalTime(verdict.auditedAt)}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

function RowDetail({ row }: { row: ParserEventRow }) {
  const flags = row.flags ?? [];
  return (
    <div
      data-testid="parser-health-row-detail"
      className="bg-gray-50 border-t border-gray-200 px-4 py-4 space-y-4"
    >
      {/* Flags */}
      <div>
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Flags ({flags.length})
        </h4>
        {flags.length === 0 ? (
          <p className="text-sm text-gray-500">No flags recorded for this parse.</p>
        ) : (
          <ul className="space-y-2">
            {flags.map((f, i) => (
              <li key={`${f.check}-${i}`} className="bg-white rounded-md border border-gray-200 p-3">
                <div className="flex items-start gap-2">
                  <SeverityBadge severity={f.severity} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">{f.message}</p>
                    <p className="mt-1 text-xs text-gray-500 font-mono break-words">
                      {compactDetail(f.detail)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* AI Verdict (Layer 3, shadow mode) */}
      <AiVerdictDetail verdict={row.ai_verdict} state={aiVerdictState(row)} />

      {/* Metadata grid */}
      <div>
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Parse metadata</h4>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
          <MetaField label="Chosen sheet" value={orDash(row.chosen_sheet)} />
          <MetaField label="Sheet count" value={orDash(row.sheet_count)} />
          <MetaField label="Header row index" value={orDash(row.header_row_index)} />
          <MetaField
            label="Header confidence"
            value={row.header_confidence == null ? DASH : row.header_confidence}
          />
          <MetaField label="Timestamp column" value={orDash(row.timestamp_column)} />
          <MetaField label="Demand unit" value={orDash(row.demand_unit)} />
          <MetaField label="Interval (min)" value={orDash(row.interval_minutes)} />
          <MetaField
            label="Rows / expected"
            value={
              row.row_count == null && row.expected_row_count == null
                ? DASH
                : `${row.row_count ?? DASH} / ${row.expected_row_count ?? DASH} (${coveragePercent(
                    row.row_count,
                    row.expected_row_count
                  )})`
            }
          />
          <MetaField label="Span (days)" value={orDash(row.span_days)} />
          <MetaField label="File size" value={formatBytes(row.file_size_bytes)} />
          <MetaField label="File type" value={orDash(row.file_type)} />
          <MetaField
            label="Format fingerprint"
            value={
              row.format_fingerprint ? (
                <span className="font-mono text-xs" title={row.format_fingerprint}>
                  {row.format_fingerprint.slice(0, 12)}…
                </span>
              ) : (
                DASH
              )
            }
          />
          {row.corrected_demand_column != null && (
            <MetaField label="Corrected demand column" value={row.corrected_demand_column} />
          )}
        </dl>
      </div>
    </div>
  );
}

export default function AdminParserHealth() {
  const [summary, setSummary] = useState<SummaryStats | null>(null);
  const [rows, setRows] = useState<ParserEventRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, UserProfileLite>>({});
  const [profileFallback, setProfileFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filters
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Layer 3: show only rows where the AI auditor disagreed with the parser.
  const [aiDisagreementsOnly, setAiDisagreementsOnly] = useState(false);

  // CI test-account exclusion (display-side). ciUserIds are resolved once from
  // CI_TEST_ACCOUNT_EMAILS; showCiTraffic (default off) re-includes them.
  const [ciUserIds, setCiUserIds] = useState<string[]>([]);
  const [showCiTraffic, setShowCiTraffic] = useState(false);

  // Resolve CI test emails → user_ids via a single batched user_profiles read.
  // Non-fatal on error/RLS: we simply skip the exclusion rather than block.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: ciErr } = await supabase
        .from('user_profiles')
        .select('id, email')
        .in('email', CI_TEST_ACCOUNT_EMAILS);
      if (cancelled || ciErr) return;
      setCiUserIds(((data ?? []) as { id: string }[]).map(r => r.id));
    })();
    return () => { cancelled = true; };
  }, []);

  // Applied to every stat-card aggregate and the events query. Excludes CI
  // rows unless the reviewer opts to show them.
  const excludeCiTraffic = useCallback(
    (q: any) =>
      !showCiTraffic && ciUserIds.length > 0
        ? q.not('user_id', 'in', `(${ciUserIds.join(',')})`)
        : q,
    [showCiTraffic, ciUserIds]
  );

  // Batched user_profiles lookup for a page of events. One query keyed by
  // user_id — never one query per row. Falls back to a truncated user_id (and
  // records the fallback) if RLS blocks the admin from reading user_profiles.
  const hydrateProfiles = useCallback(
    async (pageRows: ParserEventRow[]) => {
      const known = new Set(Object.keys(profiles));
      const missing = [
        ...new Set(
          pageRows
            .map(r => r.user_id)
            .filter((id): id is string => !!id && !known.has(id))
        ),
      ];
      if (missing.length === 0) return;
      const { data, error: profErr } = await supabase
        .from('user_profiles')
        .select('id, email, full_name')
        .in('id', missing);
      if (profErr) {
        setProfileFallback(true);
        return;
      }
      const next: Record<string, UserProfileLite> = {};
      for (const p of (data ?? []) as UserProfileLite[]) next[p.id] = p;
      // Any id we asked for but did not get back is not readable via RLS.
      if (missing.some(id => !next[id])) setProfileFallback(true);
      setProfiles(prev => ({ ...prev, ...next }));
    },
    [profiles]
  );

  const loadSummary = useCallback(async () => {
    const since = thirtyDaysAgoISO();
    const countQuery = (build: (q: any) => any) =>
      build(excludeCiTraffic(supabase.from('parser_events').select('*', { count: 'exact', head: true }).gte('created_at', since)));

    const [total, flagged, disputed, manualOrError] = await Promise.all([
      countQuery(q => q),
      countQuery(q => q.gt('flag_count', 0)),
      countQuery(q => q.eq('user_action', 'disputed')),
      countQuery(q => q.neq('parse_status', 'success')),
    ]);

    const firstErr = [total, flagged, disputed, manualOrError].find(r => r.error);
    if (firstErr?.error) throw firstErr.error;

    setSummary({
      total: total.count ?? 0,
      flagged: flagged.count ?? 0,
      disputed: disputed.count ?? 0,
      manualOrError: manualOrError.count ?? 0,
    });
  }, [excludeCiTraffic]);

  // Fetch a page of events. `cursor` is the created_at of the last loaded row;
  // pass null for the first page. Applies the active filters server-side.
  const fetchPage = useCallback(
    async (cursor: string | null): Promise<ParserEventRow[]> => {
      let q = supabase
        .from('parser_events')
        .select(EVENT_COLUMNS)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE);
      if (cursor) q = q.lt('created_at', cursor);
      if (flaggedOnly) q = q.gt('flag_count', 0);
      if (statusFilter !== 'all') q = q.eq('parse_status', statusFilter);
      // Disagreements-only: ai_verdict jsonb with agrees === false. This
      // naturally excludes agree/skipped/error/pending (none has agrees=false).
      if (aiDisagreementsOnly) q = q.eq('ai_verdict->>agrees', 'false');
      q = excludeCiTraffic(q);
      const { data, error: qErr } = await q;
      if (qErr) throw qErr;
      return (data ?? []) as unknown as ParserEventRow[];
    },
    [flaggedOnly, statusFilter, aiDisagreementsOnly, excludeCiTraffic]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded(null);
    try {
      const [, page] = await Promise.all([loadSummary(), fetchPage(null)]);
      setRows(page);
      setHasMore(page.length === PAGE_SIZE);
      await hydrateProfiles(page);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
    // hydrateProfiles intentionally omitted — it closes over `profiles` and we
    // do not want a reload each time the profile cache grows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSummary, fetchPage]);

  const loadMore = async () => {
    if (rows.length === 0) return;
    setLoadingMore(true);
    try {
      const cursor = rows[rows.length - 1].created_at;
      const page = await fetchPage(cursor);
      setRows(prev => [...prev, ...page]);
      setHasMore(page.length === PAGE_SIZE);
      await hydrateProfiles(page);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoadingMore(false);
    }
  };

  // Reload the event list (and summary) whenever a filter changes.
  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const flaggedPct =
    summary && summary.total > 0 ? Math.round((summary.flagged / summary.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Parser Health</h2>
        <button
          onClick={loadInitial}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Summary strip — last 30 days */}
      <div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Parses" value={summary ? summary.total : '—'} sub="Last 30 days" color="blue" />
          <StatCard
            label="Flagged"
            value={summary ? summary.flagged : '—'}
            sub={summary ? `${flaggedPct}% of parses` : 'Last 30 days'}
            color="amber"
          />
          <StatCard label="Disputed" value={summary ? summary.disputed : '—'} sub="user_action = disputed" color="red" />
          <StatCard
            label="Manual / Error"
            value={summary ? summary.manualOrError : '—'}
            sub="parse_status ≠ success"
            color="gray"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={e => setFlaggedOnly(e.target.checked)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Flagged only
        </label>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Status</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white text-gray-700"
          >
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="manual_required">Manual required</option>
            <option value="error">Error</option>
          </select>
        </div>
        <label
          className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
          title="Show only parses where the shadow-mode AI auditor disagreed with the deterministic parser."
        >
          <input
            type="checkbox"
            data-testid="parser-health-ai-toggle"
            checked={aiDisagreementsOnly}
            onChange={e => setAiDisagreementsOnly(e.target.checked)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Disagreements only
        </label>
        <label
          className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
          title="Fixture parses from CI/Playwright test accounts are hidden from the stats and table by default. Enable to inspect them."
        >
          <input
            type="checkbox"
            data-testid="parser-health-ci-toggle"
            checked={showCiTraffic}
            onChange={e => setShowCiTraffic(e.target.checked)}
            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Show CI test traffic
        </label>
        {profileFallback && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Showing truncated user IDs — user_profiles not readable
          </span>
        )}
      </div>

      {/* Events table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading parser events…</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table data-testid="parser-health-table" className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 w-8" />
                  {['Timestamp', 'User', 'Utility', 'File', 'Demand Col', 'Peak kW', 'Status', 'Flags', 'Action', 'AI'].map(h => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide text-xs whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-gray-500">
                      No parser events match these filters.
                    </td>
                  </tr>
                ) : (
                  rows.map(row => {
                    const isFlagged = (row.flag_count ?? 0) > 0;
                    const isDisputed = row.user_action === 'disputed';
                    const isOpen = expanded === row.id;
                    // Disputed rows get the strongest emphasis; flagged rows a subtle tint.
                    const emphasis = isDisputed
                      ? 'bg-red-50 border-l-4 border-red-400'
                      : isFlagged
                      ? 'bg-amber-50/60 border-l-4 border-amber-300'
                      : 'border-l-4 border-transparent';
                    return (
                      <Fragment key={row.id}>
                        <tr
                          onClick={() => setExpanded(isOpen ? null : row.id)}
                          className={`cursor-pointer hover:bg-gray-50 ${emphasis}`}
                        >
                          <td className="px-3 py-3 text-gray-400">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatLocalTime(row.created_at)}</td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap" title={row.user_id ?? undefined}>
                            {userLabel(row.user_id, row.user_id ? profiles[row.user_id] : undefined)}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{orDash(row.utility_name)}</td>
                          <td className="px-4 py-3 text-gray-700 max-w-[16rem] truncate" title={row.file_name ?? undefined}>
                            {orDash(row.file_name)}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{orDash(row.demand_column)}</td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {row.peak_kw == null ? DASH : row.peak_kw}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={row.parse_status} />
                          </td>
                          <td className="px-4 py-3">
                            {isFlagged ? (
                              <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                                <Flag className="w-3 h-3" /> {row.flag_count}
                              </span>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <ActionBadge action={row.user_action} />
                          </td>
                          <td className="px-4 py-3">
                            <AiVerdictBadge row={row} />
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={11} className="p-0">
                              <RowDetail row={row} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {rows.length} event{rows.length !== 1 ? 's' : ''} loaded
            </span>
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
